/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { Link } from "../components/ui/Link";
import { Pencil } from "lucide-react";
import {
  organizationDetail,
  updateOrganization,
  type OrganizationDetail as Detail,
  type OrganizationUpdate,
} from "../api/registry";
import { useAuth } from "../auth/AuthContext";
import { useChrome } from "../contexts/ChromeContext";
import { useToast } from "../contexts/ToastContext";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { CopyableId } from "../components/ui/CopyableId";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { Select } from "../components/ui/Select";
import { Spinner } from "../components/ui/Spinner";
import { Facts, Section } from "../components/DetailBits";
import { RegistryTable } from "../components/RegistryTable";
import { StateSelect } from "../components/StateSelect";
import { canEditRegistry } from "../components/RegistryBits";
import { PARTNER_TYPES, formatAddress, partnerTypeLabel, providerName } from "../lib/registry";
import { BusyLabel } from "../components/ui/BusyLabel";

export function ReferralOrganizationDetail() {
  const { id } = useParams();
  const { hasRole } = useAuth();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const refresh = useCallback(async () => {
    if (id === undefined) {
      return;
    }
    const result = await organizationDetail(id);
    if (result.ok) {
      setDetail(result.value);
      setError(null);
    } else {
      setError(result.error.message);
    }
  }, [id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useChrome(
    {
      back: { to: "/referrals/organizations", label: "Organizations" },
      title: detail?.organization.name,
    },
    [detail?.organization.name],
  );

  if (detail === null && error === null) {
    return (
      <div className="grid h-full place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }
  if (detail === null) {
    return <ErrorMessage message={error} className="mb-0" />;
  }

  const organization = detail.organization;
  return (
    <div className="space-y-6">
      <header className="enter flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl leading-tight text-text-primary sm:text-[1.75rem]">
            {organization.name}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Badge tone="neutral">{partnerTypeLabel(organization.partnerType)}</Badge>
            {organization.canReceiveUpdates && <Badge tone="success">Receives updates</Badge>}
            <CopyableId label="Organization" value={String(organization.id)} />
          </div>
        </div>
        {canEditRegistry(hasRole) && (
          <Button variant="secondary" size="md" onClick={() => setEditing(true)}>
            <Pencil size={15} />
            Edit
          </Button>
        )}
      </header>

      <ErrorMessage message={error} className="mb-0" />

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title="Details" className="lg:col-span-2">
          <Facts
            facts={[
              ["Type", partnerTypeLabel(organization.partnerType)],
              ["State", organization.stateCode ?? "—"],
              ["Receives updates", organization.canReceiveUpdates ? "Yes" : "No"],
              ["Address", formatAddress(organization.address) ?? "—"],
              [
                "Created",
                organization.createdAt !== null
                  ? new Date(organization.createdAt).toLocaleDateString()
                  : "—",
              ],
            ]}
          />
        </Section>

        <Section
          title={`Providers (${organization.providerCount})`}
          className="lg:col-span-2"
        >
          {detail.providers.length === 0 ? (
            <p className="text-sm text-text-tertiary">
              No providers recorded for this organization.
            </p>
          ) : (
            <RegistryTable headers={["Name", "Email", "Fax", "Updates"]}>
              {detail.providers.map((provider) => (
                <tr key={provider.id}>
                  <td className="py-2.5 pr-4">
                    <Link
                      to={`/referrals/providers/${provider.id}`}
                      className="font-medium text-accent underline-offset-2 hover:underline"
                    >
                      {providerName(provider.firstName, provider.lastName)}
                    </Link>
                  </td>
                  <td className="py-2.5 pr-4 text-text-secondary">{provider.email ?? "—"}</td>
                  <td className="py-2.5 pr-4 text-text-secondary">{provider.faxNumber ?? "—"}</td>
                  <td className="py-2.5 pr-4">
                    {provider.receiveUpdates ? <Badge tone="success">Yes</Badge> : "—"}
                  </td>
                </tr>
              ))}
            </RegistryTable>
          )}
        </Section>
      </div>

      {editing && (
        <EditOrganizationModal
          organization={organization}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void refresh();
          }}
        />
      )}
    </div>
  );
}

function EditOrganizationModal({
  organization,
  onClose,
  onSaved,
}: {
  organization: Detail["organization"];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { showToast } = useToast();
  const [name, setName] = useState(organization.name);
  const [stateCode, setStateCode] = useState(organization.stateCode ?? "");
  const [partnerType, setPartnerType] = useState(organization.partnerType ?? "OTHER");
  const [canReceiveUpdates, setCanReceiveUpdates] = useState(organization.canReceiveUpdates);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setError(null);
    const body: OrganizationUpdate = {
      name: name.trim(),
      stateCode: stateCode === "" ? null : stateCode,
      partnerType,
      canReceiveUpdates,
      address: organization.address,
    };
    const result = await updateOrganization(organization.id, body);
    setBusy(false);
    if (!result.ok) {
      setError(result.error.message);
      return;
    }
    showToast("success", "Organization updated");
    onSaved();
  }

  return (
    <Modal isOpen onClose={onClose} title="Edit organization" size="sm" asSheet={false}>
      <div className="space-y-4 px-6 py-5">
        <ErrorMessage message={error} className="mb-0" />
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <StateSelect label="State" value={stateCode} onChange={setStateCode} allowNone />
        <Select
          label="Type"
          value={partnerType}
          onChange={setPartnerType}
          options={PARTNER_TYPES.map((value) => ({ value, label: partnerTypeLabel(value) }))}
        />
        <Select
          label="Receives updates"
          value={canReceiveUpdates ? "yes" : "no"}
          onChange={(value) => setCanReceiveUpdates(value === "yes")}
          options={[
            { value: "yes", label: "Yes" },
            { value: "no", label: "No" },
          ]}
        />
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={busy || name.trim() === ""}
            onClick={() => void save()}
          >
            <BusyLabel busy={busy} busyText="Saving…">
              Save
            </BusyLabel>
          </Button>
        </div>
      </div>
    </Modal>
  );
}
